import type { Activity, Contact, CurrencyAmount } from '../../api/types';
import { formatMoney } from '@smbsuite/api/values';
import { isPastDue } from '../../time/due';
import { viewerZone } from '../../time/viewer-zone';
import { dayIn, dayStart, instantOfWall, wallIn } from '../../time/zones';

// Pure helpers for the CRM pages (unit-tested in app.test.ts).

// Money and phone numbers read the same in the phone app (TF-277): kept in libs/api.
export { formatAmounts, formatMoney, formatPhone } from '@smbsuite/api/values';

export const crmPath = {
  /** The deals page, on a given pipeline's board when there is one (TF-60). */
  deals: (tenantId: string, pipelineId?: string | null) => `/c/${tenantId}/crm/deals${pipelineId ? `?pipeline=${pipelineId}` : ''}`,
  deal: (tenantId: string, id: string) => `/c/${tenantId}/crm/deals/${id}`,
  accounts: (tenantId: string) => `/c/${tenantId}/crm/accounts`,
  account: (tenantId: string, id: string) => `/c/${tenantId}/crm/accounts/${id}`,
  accountGroup: (tenantId: string, id: string) => `/c/${tenantId}/crm/accounts/${id}/group`,
  contacts: (tenantId: string) => `/c/${tenantId}/crm/contacts`,
  contact: (tenantId: string, id: string) => `/c/${tenantId}/crm/contacts/${id}`,
  tasks: (tenantId: string) => `/c/${tenantId}/crm/tasks`,
  /** A task as a page of its own (TF-374); ?task=<id> on any page is the side sheet. */
  task: (tenantId: string, id: string) => `/c/${tenantId}/crm/tasks/${id}`,
  calendar: (tenantId: string) => `/c/${tenantId}/crm/calendar`,
  /** Importing accounts or contacts from a file (TF-164), and the list of imports. */
  importOf: (tenantId: string, entity: 'account' | 'contact') => `/c/${tenantId}/crm/import/${entity === 'account' ? 'accounts' : 'contacts'}`,
  importGoogle: (tenantId: string) => `/c/${tenantId}/crm/import/google`,
  importMicrosoft: (tenantId: string) => `/c/${tenantId}/crm/import/microsoft`,
  imports: (tenantId: string, importId?: string) => `/c/${tenantId}/crm/imports${importId ? `?import=${importId}` : ''}`,
  /** The accounts or contacts one import made. */
  imported: (tenantId: string, entity: 'account' | 'contact', importId: string) => `/c/${tenantId}/crm/${entity === 'account' ? 'accounts' : 'contacts'}?importId=${importId}`,
};

export function contactName(contact: Pick<Contact, 'firstName' | 'lastName'>): string {
  return `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
}

/** What was typed in a picker, as a new contact's names: split at the last space (TF-236). */
export function namesFromText(text: string): { firstName: string; lastName: string } {
  const typed = text.trim().replace(/\s+/g, ' ');
  const cut = typed.lastIndexOf(' ');
  return cut < 0 ? { firstName: typed, lastName: '' } : { firstName: typed.slice(0, cut), lastName: typed.slice(cut + 1) };
}

/** A website as typed ("example.lv") becomes a link that can only ever be http(s). */
export function websiteHref(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

const regionNames = new Map<string, Intl.DisplayNames>();

export function countryName(code: string | null, language: string): string {
  if (!code) return '';
  try {
    let names = regionNames.get(language);
    if (!names) regionNames.set(language, (names = new Intl.DisplayNames([language], { type: 'region' })));
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Where a timeline entry belongs, most specific first. */
export function activityRecordPath(tenantId: string, activity: Pick<Activity, 'dealId' | 'contactId' | 'accountId'>): string | null {
  if (activity.dealId) return crmPath.deal(tenantId, activity.dealId);
  if (activity.contactId) return crmPath.contact(tenantId, activity.contactId);
  if (activity.accountId) return crmPath.account(tenantId, activity.accountId);
  return null;
}

/** A task is overdue when its due time has passed and it is not done; a day's task once its day is over where the viewer is (TF-220). */
export function isOverdue(activity: Pick<Activity, 'dueAt' | 'doneAt' | 'dueDay'>, now = Date.now()): boolean {
  return isPastDue(activity, viewerZone(), now);
}

/**
 * A pipeline stage's figures as the dashboard writes them, amount first (TF-337): "€12,000 + $3,000 ·
 * 4 deals"; in a narrow place "€12K · 4" (the largest currency, "+" when there are more). Without
 * amounts, only the deals. `deals` is the count as a number and in words ("4 deals").
 */
export function stageFigures(amounts: CurrencyAmount[] | undefined, deals: { count: string; words: string }, language: string): { full: string; brief: string } {
  const some = (amounts ?? []).filter((one) => one.amount);
  if (some.length === 0) return { full: deals.words, brief: deals.words };
  const full = `${some.map((one) => formatMoney(one.amount, one.currency, language)).join(' + ')} · ${deals.words}`;
  const largest = [...some].sort((a, b) => b.amount - a.amount)[0]!;
  let compact: string;
  try {
    compact = new Intl.NumberFormat(language, { style: 'currency', currency: largest.currency, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(largest.amount);
  } catch {
    compact = formatMoney(largest.amount, largest.currency, language);
  }
  return { full, brief: `${compact}${some.length > 1 ? ' +' : ''} · ${deals.count}` };
}

/** Lists of amounts added up per currency (a dashboard's open stages). */
export function sumAmounts(lists: (CurrencyAmount[] | undefined)[]): CurrencyAmount[] {
  const sums = new Map<string, number>();
  for (const list of lists) for (const { currency, amount } of list ?? []) sums.set(currency, (sums.get(currency) ?? 0) + amount);
  return [...sums].map(([currency, amount]) => ({ currency, amount })).sort((a, b) => b.amount - a.amount);
}

/** A date and time field's wall clock ("2026-10-05T14:30") in the viewer's zone (TF-220) as the instant the API wants. */
export function localInputToIso(value: string, zone = viewerZone()): string | null {
  if (!value) return null;
  return instantOfWall(value, zone)?.toISOString() ?? null;
}

/** Tomorrow at the given hour where the viewer is (TF-220): when "call again tomorrow" (TF-45) is due. */
export function tomorrowAt(hour: number, now = new Date(), zone = viewerZone()): Date {
  const tomorrow = dayStart(zone, 1, now.getTime());
  return instantOfWall(`${dayIn(zone, tomorrow)}T${String(hour).padStart(2, '0')}:00`, zone)!;
}

/** An instant as the viewer's wall clock (TF-220), for a date and time field. */
export function isoToLocalInput(value: string | null, zone = viewerZone()): string {
  if (!value || Number.isNaN(Date.parse(value))) return '';
  return wallIn(zone, value);
}

/** "12500,50" or "12 500.50" as typed in either language, or null when empty; NaN when not a number. */
export function parseAmount(text: string): number | null {
  const cleaned = text.replace(/[\s ]/g, '').replace(',', '.');
  if (!cleaned) return null;
  return /^\d+(\.\d{1,2})?$/.test(cleaned) ? Number(cleaned) : Number.NaN;
}

/** tel: wants the digits (and a leading +) only. */
export const telHref = (value: string) => `tel:${value.replace(/[^\d+]/g, '')}`;
